import React, { useState } from "react";
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle } from "react-icons/fa";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { changePassword } from "../api/auth.api";
import { getStaffId } from "../utils/storage";
import { toast } from "../Components/Toast";

const PASSWORD_MAX = 100;
const hasNumberOrSymbol = (s) => /[\d\W]/.test(s);

const PasswordField = ({ name, label, placeholder, showKey, value, show, onChange, onToggleShow }) => (
  <div>
    <label className="block text-sm font-medium text-on-primary-fixed-var mb-2">{label}</label>
    <div className="relative">
      <input
        type={show[showKey] ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={PASSWORD_MAX}
        className="w-full p-3 pr-10 border border-surface-container rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        required
      />
      <button
        type="button"
        onClick={() => onToggleShow(showKey)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant"
      >
        {show[showKey] ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  </div>
);

const ChangePassword = () => {
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState({ current: false, new: false, confirm: false });

  const navigate = useNavigate();
  const staffId = getStaffId();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value.slice(0, PASSWORD_MAX) }));
    if (error || success) {
      setError("");
      setSuccess("");
    }
  };

  const lenOk = passwords.newPassword.length >= 8;
  const symbolOk = hasNumberOrSymbol(passwords.newPassword);
  const matchOk = passwords.newPassword === passwords.confirmPassword && passwords.confirmPassword.length > 0;

  const canSubmit =
    passwords.currentPassword &&
    lenOk &&
    symbolOk &&
    matchOk &&
    passwords.currentPassword !== passwords.newPassword &&
    !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!canSubmit) return;
    if (!staffId) {
      setError("Staff ID not found. Please log in again.");
      return;
    }

    setLoading(true);
    try {
      const response = await changePassword(staffId, {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      if (response.data.message) {
        setSuccess("Password changed successfully!");
        toast.success("Password changed successfully!");
        setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to change password. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Change Password | Elthium Health</title>
      </Helmet>
      <Header />
      <div className="flex-1 flex flex-col pb-16 lg:pb-0">
        <main className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-md bg-surface-lowest border border-surface-container rounded-2xl shadow-sm p-8">
            <div className="text-center mb-6">
              <div className="mx-auto w-14 h-14 bg-primary-fixed/40 rounded-full flex items-center justify-center mb-4">
                <FaLock className="text-primary text-xl" />
              </div>
              <h1 className="text-2xl font-bold text-on-primary-fixed-var">Change Password</h1>
              <p className="text-sm text-on-surface-variant mt-2">
                Ensure your account is using a long, random password to stay secure.
              </p>
            </div>

            {error && <div className="mb-4 p-3 bg-error-container text-error text-sm rounded-lg">{error}</div>}
            {success && <div className="mb-4 p-3 bg-primary-fixed/40 text-primary text-sm rounded-lg">{success}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <PasswordField
                name="currentPassword"
                label="Current Password"
                placeholder="Enter current password"
                showKey="current"
                value={passwords.currentPassword}
                show={show}
                onChange={handleInputChange}
                onToggleShow={(key) => setShow((s) => ({ ...s, [key]: !s[key] }))}
              />
              <PasswordField
                name="newPassword"
                label="New Password"
                placeholder="Enter new password"
                showKey="new"
                value={passwords.newPassword}
                show={show}
                onChange={handleInputChange}
                onToggleShow={(key) => setShow((s) => ({ ...s, [key]: !s[key] }))}
              />
              <ul className="text-xs space-y-1 -mt-2">
                <li className={`flex items-center gap-2 ${lenOk ? "text-primary" : "text-on-surface-variant"}`}>
                  <FaCheckCircle size={12} className={lenOk ? "opacity-100" : "opacity-30"} />
                  8–100 characters
                </li>
                <li className={`flex items-center gap-2 ${symbolOk ? "text-primary" : "text-on-surface-variant"}`}>
                  <FaCheckCircle size={12} className={symbolOk ? "opacity-100" : "opacity-30"} />
                  Contains a number or symbol
                </li>
              </ul>
              <PasswordField
                name="confirmPassword"
                label="Confirm New Password"
                placeholder="Confirm new password"
                showKey="confirm"
                value={passwords.confirmPassword}
                show={show}
                onChange={handleInputChange}
                onToggleShow={(key) => setShow((s) => ({ ...s, [key]: !s[key] }))}
              />
              <p className="text-xs text-on-surface-variant">Maximum 100 characters per field.</p>

              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full py-3 bg-primary text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Updating…" : "Update Password"}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-full py-3 border border-primary text-primary font-semibold rounded-lg hover:bg-primary-fixed/20"
              >
                Cancel
              </button>
            </form>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default ChangePassword;
